import { API_BASE_URL } from '../config';

// Broadcasts as canonical Media activities (backend core/cameraMediaActivities).
// Every row is validated before display: one typed camera reference, never a
// provider/storage URL and never an upload `files` entry.
const ID = /^[a-f0-9]{24}$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const STATES = ['starting', 'live', 'processing', 'ready', 'unavailable'];
const REASONS = ['broadcast_failed', 'no_recording', 'recording_failed', 'recording_partial', 'unknown'];
const AUDIENCES = ['public', 'friends', 'dojo', 'dojo_chief'];
const invalid = () => new Error('Video list could not be verified. Refresh to retry.');
const date = value => typeof value === 'string' && /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d\.\d{3}Z$/.test(value)
  && new Date(value).toISOString() === value;
const keys = (value, expected) => value && typeof value === 'object' && !Array.isArray(value)
  && Object.keys(value).sort().join() === [...expected].sort().join();

function readVideo(video, linked) {
  // Daily Live videos name their device (display only); never on unlinked rows.
  const device = linked && Object.prototype.hasOwnProperty.call(video || {}, 'device_name');
  const videoKeys = ['ref', 'state', 'live_since', 'ended_at', ...(video?.state === 'unavailable' ? ['reason'] : []), ...(device ? ['device_name'] : [])];
  if (!keys(video, videoKeys) || !keys(video.ref, ['kind', 'id']) || video.ref.kind !== 'camera_session' || !ID.test(video.ref.id)
    || !STATES.includes(video.state) || (video.state === 'unavailable' && !REASONS.includes(video.reason))
    || ![video.live_since, video.ended_at].every(value => value === null || date(value))
    || (device && (typeof video.device_name !== 'string' || !video.device_name.trim() || video.device_name.length > 60))) throw invalid();
  return Object.freeze({ ...video, ref: Object.freeze({ ...video.ref }) });
}

// Feed rows carry exactly one typed `data.video`. Addressed daily-parent detail
// (`{ detail: true }`) may add `data.videos` (1–20, unique, newest first, each
// individually authorized) and `videos_truncated`; legacy detail stays singleton.
export function readCameraMediaActivity(a, { detail = false } = {}) {
  const linked = Object.prototype.hasOwnProperty.call(a || {}, 'due_at');
  const parent = detail && linked && Object.prototype.hasOwnProperty.call(a?.data || {}, 'videos');
  // A daily Live parent names its immutable owner-local capture day (placement).
  const daily = linked && Object.prototype.hasOwnProperty.call(a || {}, 'capture_day');
  if (daily && !(typeof a.capture_day === 'string' && DAY.test(a.capture_day))) throw invalid();
  if (!keys(a, ['_id', 'user_id', 'type', 'name', 'status', 'created_at', 'completed_at', 'audiences', 'data', ...(linked ? ['due_at'] : []), ...(daily ? ['capture_day'] : [])])
    || !ID.test(a._id) || !ID.test(a.user_id) || a.type !== 'media' || (linked ? typeof a.name !== 'string' || !a.name.trim() || a.name.length > 120 || !['pending', 'complete'].includes(a.status) : a.name !== 'Video' || a.status !== 'complete')
    || !date(a.created_at) || (linked ? !(a.completed_at === null || date(a.completed_at)) || !(a.due_at === null || date(a.due_at)) : a.completed_at !== a.created_at)
    || !keys(a.data, parent ? ['video', 'videos', 'videos_truncated'] : ['video'])
    || !Array.isArray(a.audiences) || a.audiences.some(x => !AUDIENCES.includes(x))
    || new Set(a.audiences).size !== a.audiences.length) throw invalid();
  const video = readVideo(a.data.video, linked);
  if (!linked && video.ref.id !== a._id) throw invalid();
  let videos;
  if (parent) {
    if (!Array.isArray(a.data.videos) || !a.data.videos.length || a.data.videos.length > 20 || typeof a.data.videos_truncated !== 'boolean') throw invalid();
    videos = a.data.videos.map(v => readVideo(v, linked));
    if (new Set(videos.map(v => v.ref.id)).size !== videos.length || videos[0].ref.id !== video.ref.id) throw invalid();
  }
  return Object.freeze({ ...a, audiences: Object.freeze([...a.audiences]),
    data: Object.freeze(parent ? { video, videos: Object.freeze(videos), videos_truncated: a.data.videos_truncated } : { video }) });
}

// Bound to the signed-in account: a replaced login discards late responses.
export function createCameraMediaActivitiesApi({ backendService, identity, ownerId, isCurrent = () => true, timeoutMs = 10000 }) {
  if (!backendService?.client || !identity || !ID.test(ownerId || '')) throw invalid();
  const origin = new URL(API_BASE_URL || window.location.origin).origin;
  const current = () => isCurrent() && backendService.authenticatedIdentity === identity && backendService.positionOwner === ownerId;
  return Object.freeze({
    async list(scope, cursor = null) {
      if (!['mine', 'feed'].includes(scope) || !current()) throw new Error('Account or video view changed.');
      const response = await backendService.client.request({ method: 'get', url: `${origin}/api/camera/media-activities`,
        params: { scope, ...(cursor ? { cursor } : {}) }, timeout: timeoutMs, withCredentials: false,
        katafitEventIdentity: identity, katafitEventOwner: ownerId, katafitEventPosition: null });
      if (!current()) throw new Error('Account or video view changed.');
      const body = response?.data;
      if (response?.status !== 200 || !Array.isArray(body?.activities) || body.activities.length > 50
        || typeof body.exhausted !== 'boolean' || (body.nextCursor !== null && typeof body.nextCursor !== 'string')) throw invalid();
      const activities = Array.from(body.activities, readCameraMediaActivity);
      if (scope === 'mine' && activities.some(a => a.user_id !== ownerId)) throw invalid();
      return { activities, nextCursor: body.nextCursor, exhausted: body.exhausted };
    },
  });
}

// Owner complete/reopen of a persisted linked camera parent through the ordinary
// activity routes' narrow camera branch. Bound to the signed-in account like the
// list; a definite server answer is distinguished from an unknown outcome (lost
// ACK, timeout, 5xx), which the caller must reconcile from persisted state.
export class CameraMediaStatusError extends Error {
  constructor(outcome, message) { super(message); this.outcome = outcome; }
}
export async function setCameraMediaActivityStatus({ backendService, identity, ownerId, isCurrent = () => true, timeoutMs = 10000 }, activityId, transition) {
  if (!backendService?.client || !identity || !ID.test(ownerId || '') || !ID.test(activityId || '') || !['complete', 'reopen'].includes(transition)) {
    throw new CameraMediaStatusError('rejected', 'This activity could not be updated.');
  }
  const current = () => isCurrent() && backendService.authenticatedIdentity === identity && backendService.positionOwner === ownerId;
  if (!current()) throw new CameraMediaStatusError('stale', 'Account changed.');
  const origin = new URL(API_BASE_URL || window.location.origin).origin;
  let response;
  try {
    response = await backendService.client.request({ method: 'put', url: `${origin}/api/activities/${activityId}/${transition}`,
      timeout: timeoutMs, withCredentials: false, katafitEventIdentity: identity, katafitEventOwner: ownerId, katafitEventPosition: null });
  } catch (error) {
    const status = error?.response?.status;
    if (!current()) throw new CameraMediaStatusError('stale', 'Account changed.');
    if (status >= 400 && status < 500) throw new CameraMediaStatusError('rejected', error.response.data?.error || 'This activity could not be updated.');
    throw new CameraMediaStatusError('unknown', 'Saving could not be confirmed.');
  }
  if (!current()) throw new CameraMediaStatusError('stale', 'Account changed.');
  const body = response?.data;
  const status = transition === 'complete' ? 'complete' : 'pending';
  if (response?.status !== 200 || body?._id !== activityId || body.type !== 'media' || body.status !== status
    || !(transition === 'complete' ? date(body.completed_at) : body.completed_at === null)) {
    throw new CameraMediaStatusError('unknown', 'Saving could not be confirmed.');
  }
  return { _id: body._id, status: body.status, completed_at: body.completed_at };
}
